import { useEffect, useRef, useState, useCallback } from "react";
import { lightingDetectionService } from "../services/lightingDetection.service";

/**
 * Custom hook for lighting detection
 * @param {Object} options - Configuration options
 * @param {HTMLVideoElement} options.videoElement - Video element to process
 * @param {boolean} options.enabled - Whether lighting detection is enabled
 * @param {number} options.throttleMs - Throttle interval (default: 500ms)
 * @param {boolean} options.useExternalLoop - When true, disables internal RAF loop
 * @param {Object} options.faceBoundingBoxRef - Ref containing face bounding box for smart sampling
 * @returns {Object} Lighting detection state
 */
export function useLightingDetection({
  videoElement,
  enabled = true,
  throttleMs = 500,
  useExternalLoop = false,
  faceBoundingBoxRef = null,
}) {
  const [lightingResult, setLightingResult] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const lastDetectionTimeRef = useRef(0);
  const animationFrameRef = useRef(null);

  const detectLighting = useCallback(() => {
    if (!videoElement || !enabled) return;

    const now = performance.now();
    if (now - lastDetectionTimeRef.current < throttleMs) return;

    lastDetectionTimeRef.current = now;

    try {
      setIsDetecting(true);
      const faceBoundingBox = faceBoundingBoxRef?.current ?? null;
      const result = lightingDetectionService.detectLighting(
        videoElement,
        faceBoundingBox,
      );
      if (result !== null) {
        setLightingResult(result);
      }
    } catch (error) {
      console.error("Lighting detection error:", error);
    } finally {
      setIsDetecting(false);
    }
  }, [videoElement, enabled, throttleMs, faceBoundingBoxRef]);

  useEffect(() => {
    if (!enabled || !videoElement) {
      setLightingResult(null);
      lightingDetectionService.resetHistory();
      return;
    }

    if (useExternalLoop) {
      return;
    }

    const loop = () => {
      detectLighting();
      animationFrameRef.current = requestAnimationFrame(loop);
    };

    animationFrameRef.current = requestAnimationFrame(loop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [enabled, videoElement, detectLighting, useExternalLoop]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    lightingResult,
    isDetecting,
    detectLighting,
  };
}
